import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { School, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Page « Mes classes » : l'enseignant voit toutes ses classes, l'élève uniquement les siennes
export default function ClassesPage() {
  const { tspace, t } = useAuth();
  const isTeacher = tspace?.my_role === "teacher";
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", instrument: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/teaching/classes").then((r) => setClasses(r.data.classes)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/teaching/classes", form);
      toast.success(t("teach.saved"));
      setOpen(false);
      setForm({ name: "", description: "", instrument: "" });
      load();
    } catch (e) { toast.error(e.friendly); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="teach-classes-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
          <School className="h-6 w-6 brand" /> {isTeacher ? t("teach.classes_title") : t("teach.nav_my_classes")}
        </h2>
        {isTeacher && (
          <Button className="brand-bg text-white" onClick={() => setOpen(true)} data-testid="add-class-button">
            <Plus className="h-4 w-4 mr-1" /> {t("teach.add_class")}
          </Button>
        )}
      </div>

      {classes.length === 0 ? (
        <p className="text-sm t-secondary py-8 text-center" data-testid="classes-empty">
          {isTeacher ? t("teach.no_classes") : t("teach.no_class_student")}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="classes-list">
          {classes.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/enseignement/classes/${c.id}`)}
              className="app-card p-5 text-left hover:border-app transition-colors"
              data-testid={`class-card-${c.id}`}
            >
              <p className="font-heading font-bold truncate">{c.name}</p>
              {c.instrument && <p className="text-xs t-secondary mt-0.5">{c.instrument}</p>}
              {c.description && <p className="text-xs t-secondary mt-1 line-clamp-2">{c.description}</p>}
              <p className="text-xs brand mt-2 font-semibold">{t(c.member_count > 1 ? "teach.member_count_p" : "teach.member_count_s", { n: c.member_count })}</p>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-bg border-app" data-testid="add-class-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("teach.add_class")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.class_name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="class-name-input" autoFocus />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.description")}</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="class-desc-input" />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("param.instrument_one")}</Label>
              <Input value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="class-instrument-input" />
            </div>
            <Button className="w-full brand-bg text-white" onClick={create} disabled={!form.name.trim() || saving} data-testid="create-class-confirm">
              {t("common.create")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
